"use client";
import React from "react";
import { useTranslations } from "next-intl";
import { useMutation, useQuery } from "@apollo/client";
import { useTheme } from "@quales/utils/theme";
import { useWindowDimensions } from "@quales/utils/hooks";
import {
  UpdateCustomFormDocument,
  UpdateTenantFormDocument,
  UpdateTenantFormPartsByFormIdDocument,
  AllTenantFormsDocument,
  AllTenantFormsRow,
} from "@quales/data-access/gql-server";
import { ModalButtonBar } from "../ui/button";
import ConfigTenantFormParts from "./ConfigTenantFormPartsComposite";
import {
  WcFormLabel,
  WcModal,
  WcStack,
  WcSwitch,
  WcTypography,
} from "@bpipartners/webui";
import {
  WcGridActionsCellItem,
  WcGridColDef,
  WcGridRenderCellParams,
  WcGridRowParams,
  WcDataGrid,
} from "@bpipartners/datagrid";
import { Header } from "../ui/header";
import { enqueueSnackbar } from "notistack";
import { PiXLight, PiCheck, PiNotePencil, PiGearFill } from "react-icons/pi";

// import { TourContext } from "@quales/utils/context";
// import { UserTour } from "./TourData";

export const TenantFormsComposite = () => {
  const t = useTranslations();
  const theme = useTheme();
  const windowSize = useWindowDimensions();

  const configFormPartsMapRef = React.useRef();

  const [formSettingsModalOpen, setFormSettingsModalOpen] =
    React.useState(false);
  const [configModalOpen, setConfigModalOpen] = React.useState(false);

  const [currentFormID, setCurrentFormID] = React.useState();
  const [currentForm, setCurrentForm] = React.useState<AllTenantFormsRow>();

  const [tenantFormList, setTenantFormList] = React.useState<
    AllTenantFormsRow[]
  >([]);

  const { loading: tenantFormLoading, data: tenantFormData } = useQuery(
    AllTenantFormsDocument,
    { fetchPolicy: "network-only" }
  );

  // const { tour } = React.useContext(TourContext);
  // const [tourRunning, setTourRunning] = React.useState(false);

  // React.useEffect(() => {
  //   if (tour) {
  //     setTourRunning(true);
  //   }
  // }, [tour]);

  // const handleTour = (tourID: string) => {
  //   switch (tourID) {
  //     case "2":
  //       return <UserTour />;

  //     default:
  //       return <></>;
  //   }
  // };

  React.useEffect(() => {
    if (
      !tenantFormLoading &&
      tenantFormData &&
      tenantFormData.AllTenantForms &&
      tenantFormData.AllTenantForms.length > 0
    ) {
      setTenantFormList(tenantFormData.AllTenantForms);
    } else {
      setTenantFormList([]);
    }
  }, [tenantFormData, tenantFormLoading]);

  const handleOnRoleMutationCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.updateSuccess"), {
        variant: "success",
      });
      setFormSettingsModalOpen(false);
      setConfigModalOpen(false);
      setCurrentFormID(null);
      setCurrentForm(null);
    }, 1000);
  };

  const onModalPressed = React.useCallback(
    (param: WcGridRowParams, modalType) => () => {
      switch (modalType) {
        case "config":
          setConfigModalOpen(true);
          setCurrentFormID(param.row.formID);
          return;
        case "formSettings":
          setFormSettingsModalOpen(true);
          setCurrentFormID(param.row.formID);
          setCurrentForm(param.row);
          return;
        default:
          return;
      }
    },
    []
  );

  const handleConfigFormSubmit = () => {
    if (configFormPartsMapRef && configFormPartsMapRef.current) {
      //@ts-ignore
      configFormPartsMapRef.current.onSubmit();
    }
  };

  const [updateTenantFormParts] = useMutation(
    UpdateTenantFormPartsByFormIdDocument,
    {
      onCompleted: handleOnRoleMutationCompleted,
    }
  );

  const [updateTenantForm] = useMutation(UpdateTenantFormDocument, {
    onCompleted: handleOnRoleMutationCompleted,
    refetchQueries: [
      {
        query: AllTenantFormsDocument,
      },
    ],
  });

  const [updateCustomForm] = useMutation(UpdateCustomFormDocument, {
    onCompleted: handleOnRoleMutationCompleted,
    refetchQueries: [
      {
        query: AllTenantFormsDocument,
      },
    ],
  });

  const handleEnabledFormToggle = () => {
    setCurrentForm({ ...currentForm, enabled: !currentForm.enabled });
  };

  const handleFormSettingsClose = () => {
    setFormSettingsModalOpen(false);
    setCurrentFormID(null);
    setCurrentForm(null);
  };

  const handleFormSettingsSubmit = () => {
    if (currentForm.customForm) {
      updateCustomForm({
        variables: {
          formID: currentFormID,
          enabled: currentForm.enabled,
        },
      });
    } else {
      updateTenantForm({
        variables: {
          formID: currentFormID,
          enabled: currentForm.enabled,
        },
      });
    }
  };

  const handleFormPartsConfig = (items: any[]) => {
    const formParts = items.map((item: any) => item.formPartID);
    updateTenantFormParts({
      variables: {
        formID: currentFormID,
        formParts: formParts,
      },
    });
  };

  const TenantColumnData: WcGridColDef[] = [
    {
      field: "formID",
      headerName: t("labels.ID"),
      minWidth: 60,
      flex: 0.1,
      editable: false,
      sortable: true,
    },
    {
      field: "name",
      headerName: t("labels.name"),
      minWidth: 120,
      flex: 0.8,
      sortable: true,
      editable: false,
    },
    {
      field: "enabled",
      headerName: t("labels.active"),
      minWidth: 60,
      flex: 0.1,
      renderCell: (params: WcGridRenderCellParams) => (
        <>
          {params.row.enabled ? (
            <PiCheck size={18} color={theme.palette.success.main} />
          ) : (
            <PiXLight size={18} color={theme.palette.error.main} />
          )}
        </>
      ),
      sortable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("labels.actions"),
      minWidth: 100,
      flex: 0.3,
      getActions: (params: WcGridRowParams) => [
        <WcGridActionsCellItem
          disabled={!params.row.isConfigurable}
          key={1}
          icon={
            <PiNotePencil
              color={params.row.isConfigurable ? "primary" : "disabled"}
              title={t("common.manageFormConfig")}
              size={18}
            />
          }
          label={t("common.manageFormConfig")}
          onClick={onModalPressed(params, "config")}
        />,
        <WcGridActionsCellItem
          key={2}
          label={t("common.formSettings")}
          icon={<PiGearFill title={t("common.formSettings")} size={18} />}
          onClick={onModalPressed(params, "formSettings")}
        />,
      ],
    },
  ];

  return (
    <WcStack id="TenantFormsComposite">
      <Header
        pageTitle={t("common.tenantForms")}
        pageTitleTypographyProps={{
          variant: "h5",
          color: theme.appColors.primary,
        }}
      >
        <></>
      </Header>

      <WcStack
        width={"100%"}
        height={windowSize.height - theme.dimensions.totalTopbarHeight}
      >
        {!tenantFormLoading && (
          <WcDataGrid
            columns={TenantColumnData}
            rowHeight={60}
            disableColumnMenu
            getCustomID={(row) => row.formID}
            rows={tenantFormList}
          />
        )}
      </WcStack>

      {/* {tourRunning && handleTour(tour.tourID)} */}

      {formSettingsModalOpen && (
        <WcModal
          modalTitle={t("common.formSettings")}
          open={formSettingsModalOpen}
          minWidth={600}
          closeHandler={() => handleFormSettingsClose()}
        >
          <>
            <WcStack direction={"row"} justifyContent="space-between">
              <WcTypography variant="h6">{currentForm.name}</WcTypography>

              <ModalButtonBar
                disabled={false}
                onEdit={() => null}
                onSave={() => handleFormSettingsSubmit()}
                isCreate={true}
              />
            </WcStack>

            <WcStack>
              <WcFormLabel>{t("labels.active")}</WcFormLabel>
              <WcSwitch
                checked={currentForm.enabled}
                onClick={handleEnabledFormToggle}
              ></WcSwitch>
            </WcStack>
          </>
        </WcModal>
      )}

      {configModalOpen && (
        <WcModal
          modalTitle={t("common.manageFormConfig")}
          open={configModalOpen}
          minWidth={600}
          closeHandler={() => setConfigModalOpen(false)}
        >
          <WcStack>
            <WcStack direction={"row"} justifyContent="flex-end">
              <ModalButtonBar
                disabled={false}
                onEdit={() => null}
                onSave={() => handleConfigFormSubmit()}
                isCreate={true}
              />
            </WcStack>
            <ConfigTenantFormParts
              formID={currentFormID}
              ref={configFormPartsMapRef}
              onSubmitClicked={handleFormPartsConfig}
            />
          </WcStack>
        </WcModal>
      )}
    </WcStack>
  );
};

export default TenantFormsComposite;
